// Multi-route selection for the outing forms: a typeahead that appends to an
// ordered list, each row with optional climbing style (enum) and result
// (free-text column). List order becomes the outing_route `sort` value.
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { gqlRequest } from "../lib/gql";
import { ROUTE_PICKER_SEARCH, type RoutePickerResult, type RouteRef } from "../lib/queries";
import { CLIMB_STYLES, type ClimbStyle } from "../lib/format";
import { useT } from "../lib/i18n";
import { Select, TextInput } from "./ui";

export type RouteLink = {
  route: RouteRef;
  style: ClimbStyle | "";
  result: string;
};

export function RouteLinkPicker({
  links,
  onChange,
  onAddRoute,
  simple = false,
}: {
  links: RouteLink[];
  onChange: (links: RouteLink[]) => void;
  /** Extra hook for pages that react to a route being added (e.g. activity prefill). */
  onAddRoute?: (route: RouteRef) => void;
  /** Hide the per-route style/result inputs — event route links carry neither. */
  simple?: boolean;
}) {
  const { t, labelizeT } = useT();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const suggestions = useQuery({
    queryKey: ["route-picker", debounced],
    enabled: debounced.length >= 2,
    queryFn: () => gqlRequest<RoutePickerResult>(ROUTE_PICKER_SEARCH, { q: `%${debounced}%` }),
  });

  const selectedIds = new Set(links.map((l) => l.route.id));
  const candidates = suggestions.data?.routes.nodes.filter((r) => !selectedIds.has(r.id)) ?? [];

  function add(route: RouteRef) {
    onChange([...links, { route, style: "", result: "" }]);
    onAddRoute?.(route);
    setQuery("");
    setDebounced("");
  }

  function patch(index: number, changes: Partial<RouteLink>) {
    onChange(links.map((l, i) => (i === index ? { ...l, ...changes } : l)));
  }

  return (
    <div className="space-y-2">
      {links.length > 0 && (
        <ul className="space-y-2">
          {links.map((l, i) => (
            <li key={l.route.id} className="rounded-xl border border-primary/25 bg-primary/5 px-3 py-2">
              <div className="flex items-center justify-between">
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-foreground">{l.route.name}</span>
                  <span className="block text-xs text-muted-foreground">{l.route.activities.map(labelizeT).join(", ")}</span>
                </span>
                <button
                  type="button"
                  onClick={() => onChange(links.filter((_, j) => j !== i))}
                  className="ml-2 shrink-0 text-muted-foreground transition-colors hover:text-destructive"
                  aria-label={t("picker.removeRouteAria", { name: l.route.name })}
                >
                  <X aria-hidden className="size-4" />
                </button>
              </div>
              {!simple && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <Select
                    value={l.style}
                    aria-label={t("picker.styleAria")}
                    onChange={(e) => patch(i, { style: e.target.value as ClimbStyle | "" })}
                    className="h-8 text-xs"
                  >
                    <option value="">{t("picker.stylePlaceholder")}</option>
                    {CLIMB_STYLES.map((s) => (
                      <option key={s} value={s}>
                        {labelizeT(s)}
                      </option>
                    ))}
                  </Select>
                  <TextInput
                    value={l.result}
                    aria-label={t("picker.resultAria")}
                    placeholder={t("picker.resultPlaceholder")}
                    onChange={(e) => patch(i, { result: e.target.value })}
                    className="h-8 text-xs"
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="relative">
        <TextInput
          value={query}
          placeholder={links.length ? t("picker.addAnotherRoute") : t("picker.searchRoutes")}
          onChange={(e) => setQuery(e.target.value)}
        />
        {debounced.length >= 2 && suggestions.data && (
          <div className="absolute top-full z-10 mt-1.5 max-h-56 w-full divide-y divide-border overflow-y-auto rounded-xl border bg-popover text-popover-foreground shadow-panel">
            {candidates.length === 0 && <p className="p-3 text-sm text-muted-foreground">{t("picker.noRoutes")}</p>}
            {candidates.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => add(r)}
                className="block w-full px-3 py-2 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <span className="block truncate font-medium text-foreground">{r.name}</span>
                <span className="text-xs text-muted-foreground">{r.activities.map(labelizeT).join(", ")}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
